import React, { useState, useEffect } from 'react';

import { ScrollArea } from "@/components/ui/scroll-area";

import { useRecoilState } from 'recoil';
import { outletAtom } from 'core/config/atoms';

import { motion } from "framer-motion";
import { toast } from 'react-toastify';
import Lottie from 'lottie-react';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import animBtnLoading from 'assets/anim/anim-btnLoading.json';
import ServiceOutlet from 'services/serviceOutlet';

export default function PanelSettingOutlet(props) {

    const [outletData, setOutletData] = useRecoilState(outletAtom);

    const [submit, setSubmit] = useState(false);

    const outletService = new ServiceOutlet();

    const formVSchema = Yup.object().shape({
        name: Yup.string().required('This information is required'),
        tname: Yup.string().required('This information is required'),
        title: Yup.string().required('This information is required'),
        email: Yup.string().required('This information is required').email('Please enter valid email address'),
        phone: Yup.string().required('This information is required').matches(/^\d{10}$/, 'Phone number must be 10 digits'),
        street_1: Yup.string().required('This information is required'),
        street_2: Yup.string().required('This information is required'),
        city: Yup.string().required('This information is required'),
        state: Yup.string().required('This information is required'),
        country: Yup.string().required('This information is required'),
        postal_code: Yup.string().required('This information is required').matches(/^\d{6}$/, 'Phone number must be 10 digits'),
        upiId: Yup.string().required('This information is required')
    });

    const { values, errors, touched, handleChange, handleSubmit, handleReset, resetForm, setErrors, setValues } = useFormik({
        initialValues: {
            name: '',
            tname: '',
            title: '',
            email: '',
            phone: '',
            street_1: '',
            street_2: '',
            city: '',
            state: '',
            country: '',
            postal_code: '',
            upiId: ''
        },
        validationSchema: formVSchema,
        enableReinitialize: true,
        onSubmit: values => {
            let body = {
                uid: outletData.outletid.id,
                data: {
                    "name": values.name,
                    "tradNme": values.tname,
                    "title": values.title,
                    "phone": values.phone,
                    "email": values.email,
                    "upi": values.upiId,
                    "addresses": {
                        "title": "Office",
                        "default": true,
                        "street_1": values.street_1,
                        "street_2": values.street_2,
                        "city": values.city,
                        "state": values.state,
                        "country": values.country,
                        "postal_code": values.postal_code,
                    }
                }
            }
            outletService.updateOutletById(body).then((res) => {
                setSubmit(false);
                if (res.status) {
                    toast.success("Outlet updated!", { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
                    setOutletData({
                        ...outletData,
                        outletid: {
                            ...outletData.outletid,
                            upi: values.upiId
                        }
                    });
                    loadData();
                    handleReset();
                    resetForm();
                } else {
                    toast.error('Server error please try again', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
                }
            });
        }
    });

    const loadData = () => {
        (async function () {
            outletService.getOutletById({ "outletid": outletData.outletid.outletid }).then((res) => {
                const outlet = res.data;
                setValues({
                    name: outlet.name,
                    tname: outlet.tradNme,
                    title: outlet.title,
                    email: outlet.email,
                    phone: outlet.phone,
                    upiId: outlet.upi,
                    street_1: outlet.addresses[0].street_1,
                    street_2: outlet.addresses[0].street_2,
                    city: outlet.addresses[0].city,
                    state: outlet.addresses[0].state,
                    country: outlet.addresses[0].country,
                    postal_code: outlet.addresses[0].postal_code
                });
            });
        })();
    }

    useEffect(() => {
        loadData();
    }, []);

    return (
        <div className="w-full h-full py-3 px-3">
            <div className='w-full min-h-screen'>
                <ScrollArea>
                    <div className='px-2'>
                        <h1 className='text-2xl font-sans font-medium text-gray-900 uppercase'>
                            <motion.i whileHover={{ scale: 1.2 }} className="las la-bars cursor-pointer" onClick={() => { props.onToggle(); }}></motion.i>UPDATE OUTLET
                        </h1>
                        <hr className='my-2' />
                        <form className='mt-4 grid grid-cols-12 gap-3' onSubmit={handleSubmit} onReset={handleReset} noValidate="" autoComplete='off'>
                            <div className="col-span-4">
                                <label className="block mb-2 text-sm font-medium text-gray-600">Outlet Name<sup className="text-red-600">*</sup></label>
                                <input type="text" id="name" value={values.name} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. Hotel Name" onChange={handleChange} />
                                {(errors.name && touched.name) && <p className='text-xs text-red-400 mt-1'>{errors.name}</p>}
                            </div>

                            <div className="col-span-4">
                                <label className="block mb-2 text-sm font-medium text-gray-600">Outlet Trade Name<sup className="text-red-600">*</sup></label>
                                <input type="text" id="tname" value={values.tname} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. Hotel Trade Name" onChange={handleChange} />
                                {(errors.tname && touched.tname) && <p className='text-xs text-red-400 mt-1'>{errors.tname}</p>}
                            </div>

                            <div className="col-span-4">
                                <label className="block mb-2 text-sm font-medium text-gray-600">Outlet Title<sup className="text-red-600">*</sup></label>
                                <input type="text" id="title" value={values.title} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. Hotel Trade Name" onChange={handleChange} />
                                {(errors.title && touched.title) && <p className='text-xs text-red-400 mt-1'>{errors.title}</p>}
                            </div>

                            <div className="col-span-4">
                                <label className="block mb-2 text-sm font-medium text-gray-600">Email Address<sup className="text-red-600">*</sup></label>
                                <input type="email" id="email" value={values.email} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. jhon.deo@gmail.com" onChange={handleChange} />
                                {(errors.email && touched.email) && <p className='text-xs text-red-400 mt-1'>{errors.email}</p>}
                            </div>

                            <div className="col-span-4">
                                <label className="block mb-2 text-sm font-medium text-gray-600">Phone Number<sup className="text-red-600">*</sup></label>
                                <input type="number" id="phone" value={values.phone} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. xxxxxxxxxx" onChange={handleChange} />
                                {(errors.phone && touched.phone) && <p className='text-xs text-red-400 mt-1'>{errors.phone}</p>}
                            </div>

                            <div className="col-span-4">
                                <label className="block mb-2 text-sm font-medium text-gray-600">UPI ID<sup className="text-red-600">*</sup></label>
                                <input type="text" id="upiId" value={values.upiId} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. example@upi" onChange={handleChange} />
                                {(errors.upiId && touched.upiId) && <p className='text-xs text-red-400 mt-1'>{errors.upiId}</p>}
                            </div>

                            <div className="col-span-12">
                                <label className="block mb-2 text-sm font-medium text-gray-600">Street Address 1<sup className="text-red-600">*</sup></label>
                                <input type="text" id="street_1" value={values.street_1} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. Apt, Building " onChange={handleChange} />
                                {(errors.street_1 && touched.street_1) && <p className='text-xs text-red-400 mt-1'>{errors.street_1}</p>}
                            </div>

                            <div className="col-span-12">
                                <label className="block mb-2 text-sm font-medium text-gray-600">Street Address 2<sup className="text-red-600">*</sup></label>
                                <input type="text" id="street_2" value={values.street_2} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. Street address, Landmark" onChange={handleChange} />
                                {(errors.street_2 && touched.street_2) && <p className='text-xs text-red-400 mt-1'>{errors.street_2}</p>}
                            </div>

                            <div className="col-span-4">
                                <label className="block mb-2 text-sm font-medium text-gray-600">City<sup className="text-red-600">*</sup></label>
                                <input type="text" id="city" value={values.city} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. Mumbai" onChange={handleChange} />
                                {(errors.city && touched.city) && <p className='text-xs text-red-400 mt-1'>{errors.city}</p>}
                            </div>

                            <div className="col-span-4">
                                <label className="block mb-2 text-sm font-medium text-gray-600">State<sup className="text-red-600">*</sup></label>
                                <input type="text" id="state" value={values.state} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. Maharashtra" onChange={handleChange} />
                                {(errors.state && touched.state) && <p className='text-xs text-red-400 mt-1'>{errors.state}</p>}
                            </div>

                            <div className="col-span-4">
                                <label className="block mb-2 text-sm font-medium text-gray-600">Country<sup className="text-red-600">*</sup></label>
                                <input type="text" id="country" value={values.country} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. India" onChange={handleChange} />
                                {(errors.country && touched.country) && <p className='text-xs text-red-400 mt-1'>{errors.country}</p>}
                            </div>

                            <div className="col-span-4">
                                <label className="block mb-2 text-sm font-medium text-gray-600">Postal code<sup className="text-red-600">*</sup></label>
                                <input type="text" id="postal_code" value={values.postal_code} autoComplete='off' className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5" placeholder="i.e. xxxxxx" onChange={handleChange} />
                                {(errors.postal_code && touched.postal_code) && <p className='text-xs text-red-400 mt-1'>{errors.postal_code}</p>}
                            </div>

                            <div className='col-span-12 my-6'>
                                <div className='flex items-end'>
                                    <button type="submit" className="text-white bg-prime hover:bg-primeLight focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center" disabled={submit}>
                                        {submit && <Lottie animationData={animBtnLoading} className="w-8 h-8" loop={true} />}
                                        {!submit && "SAVE"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </ScrollArea >
            </div>
        </div>
    )
}
